import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '../../contexts/WalletContext';
import Button from '../common/Button';
import { UserCircleIcon, ArrowRightOnRectangleIcon, WalletIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const ConnectWalletButton = () => {
  const { currentAccount, connectWallet, disconnectWallet, isLoading, error } = useWallet();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null); // Ref untuk dropdown

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDisconnect = () => {
    disconnectWallet();
    setIsDropdownOpen(false); 
  };

  const handleConnect = async () => {
    await connectWallet();
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    // Cleanup function
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);


  const formatAddress = (address) => {
    // unwrap {provider, address} or array or string
    const raw = address && typeof address === 'object' && 'address' in address
      ? address.address
      : Array.isArray(address)
        ? address[0]
        : address;
    if (!raw || typeof raw !== 'string' || raw.length < 10) return 'Invalid Address';
    return `${raw.substring(0, 6)}...${raw.substring(raw.length - 4)}`;
  };

  if (!currentAccount) {
    return (
      <div className="relative">
        <Button
          onClick={handleConnect}
          isLoading={isLoading}
          disabled={isLoading}
          variant="primary" 
          className="flex items-center !px-4 !py-2 text-sm bg-blue-500 hover:bg-blue-600 border-blue-500 hover:border-blue-600" 
        >
          <WalletIcon className="h-5 w-5 mr-2" />
          Connect Wallet
        </Button>
        {error && <p className="absolute top-full mt-1 text-xs text-red-400 whitespace-nowrap">{error}</p>}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        type="button"
        className="flex items-center space-x-2 px-3 py-1.5 bg-slate-700/50 hover:bg-slate-600/70 rounded-md text-sm font-medium text-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:ring-blue-500 transition-colors"
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
      >
        <UserCircleIcon className="h-6 w-6 text-slate-300" />
        <span>{formatAddress(currentAccount)}</span>
        <ChevronDownIcon
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
            isDropdownOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div
          className="absolute right-0 mt-2 w-60 origin-top-right rounded-md shadow-2xl 
                     bg-slate-800 ring-1 ring-black ring-opacity-5 focus:outline-none 
                     border border-slate-700 z-50 animate-fadeIn" 
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
        >
          <div className="py-1" role="none">
            <div className="px-4 py-3">
              <p className="text-xs text-slate-400">Signed in as</p>
              <p
                className="text-sm font-medium text-slate-100 truncate"
                title={formatAddress(currentAccount)}
              >
                {formatAddress(currentAccount)}
              </p>
            </div>
            <hr className="border-slate-700" />
            <Link
              to="/my-agents"
              onClick={() => setIsDropdownOpen(false)}
              className="block px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 hover:text-blue-300 w-full text-left transition-colors"
              role="menuitem"
            >
              My Agents
            </Link>
            <Link
              to="/profile" 
              onClick={() => setIsDropdownOpen(false)}
              className="block px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 hover:text-blue-300 w-full text-left transition-colors"
              role="menuitem"
            >
              Profile
            </Link>
            <hr className="border-slate-700" />
            <button
              onClick={handleDisconnect}
              className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors"
              role="menuitem"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
              Disconnect
            </button>
          </div>
        </div>
      )}
      {error && !isDropdownOpen && <p className="absolute top-full right-0 mt-1 text-xs text-red-400 whitespace-nowrap">{error}</p>}
    </div>
  );
};



export default ConnectWalletButton;